/**
 * Backtesting Guide Documentation
 *
 * How to use the backtesting interface — configuring, running, and reading results.
 */
import type { DocPage } from '../types';

export const backtestingDocs: DocPage[] = [
  {
    id: 'backtesting/running-a-backtest',
    title: 'Running a Backtest',
    category: 'backtesting',
    tags: ['backtest', 'run', 'configure', 'ui', 'symbol', 'interval', 'portfolio'],
    version: '2.0',
    related: ['backtesting/reading-results', 'backtesting/configuration', 'runs/backtest'],
    order: 1,
    description: 'Step-by-step guide to running your first backtest',
    content: `# Running a Backtest

A backtest replays your strategy against historical candle data to see how it would have traded. This is the fastest way to evaluate a strategy idea.

## Quick Start

1. Open the **Research Lab** and select a strategy
2. Switch to the **Backtest** tab in the right dock
3. Choose a symbol and interval
4. Click **Run Backtest**

Results appear immediately: equity curve, trades, and performance metrics.

## Choosing a Symbol

Quanthop supports major cryptocurrency pairs on Binance. Select a symbol from the dropdown — common choices include:

- **BTCUSDT** — Bitcoin
- **ETHUSDT** — Ethereum
- **SOLUSDT** — Solana
- **BNBUSDT** — BNB

## Choosing an Interval

The interval determines the candle timeframe your strategy operates on:

| Interval | Use Case |
|----------|----------|
| **1h** | Intraday signals, more trades, noisier |
| **4h** | Balanced frequency, popular for swing trading |
| **1d** | Daily signals, fewer trades, cleaner trends |

Shorter intervals produce more candles and more trades, but also more noise. Longer intervals are smoother but give fewer data points.

## Date Range

Set the start and end dates for the backtest period. A longer range gives more trades and more statistical significance, but also takes more time.

You can also use **candle range** mode to specify start and end points by candle index instead of dates.

## Running the Backtest

Click **Run Backtest**. The engine will:

1. Fetch historical candles for your symbol and interval
2. Execute your strategy's \`define()\`, \`init()\`, and \`onBar()\` functions
3. Simulate order fills with realistic fee assumptions
4. Return complete trade history and performance statistics

## Next Steps

- [Configuration](/docs/backtesting/configuration) — Portfolio, fees, and trading mode settings
- [Reading Results](/docs/backtesting/reading-results) — Understanding equity curves and metrics
`,
  },
  {
    id: 'backtesting/configuration',
    title: 'Configuration',
    category: 'backtesting',
    tags: ['configuration', 'portfolio', 'fees', 'trading mode', 'stop loss', 'take profit', 'slippage'],
    version: '2.0',
    related: ['backtesting/running-a-backtest', 'backtesting/reading-results', 'guides/trading-modes'],
    order: 2,
    description: 'Configuring portfolio, fees, trading mode, and risk settings',
    content: `# Configuration

Every backtest runs with a set of configuration options that control portfolio sizing, fees, and risk behavior.

## Portfolio Settings

| Setting | Default | Description |
|---------|---------|-------------|
| **Portfolio Amount** | 10,000 | Starting capital in quote currency |
| **Portfolio Currency** | Auto | Detected from the symbol (e.g., USDT for BTCUSDT) |
| **Portfolio Type** | USD | How capital is tracked — USD, ASSET, or MIXED |

**Portfolio types:**

- **USD** — Track everything in quote currency. Best for long-only strategies.
- **ASSET** — Track in base currency. Useful for accumulation strategies.
- **MIXED** — Track both. Required for short selling.

## Trading Mode

| Mode | Description |
|------|-------------|
| **Long Only** | Buy low, sell high. The default for most strategies. |
| **Short Only** | Sell high, buy low. Automatically uses MIXED portfolio. |
| **Bidirectional** | Both long and short positions allowed. |

## Fees and Slippage

| Setting | Default | Description |
|---------|---------|-------------|
| **Trading Fee** | 0.1% | Applied to each trade (entry and exit) |
| **Slippage** | 0% | Additional cost to simulate market impact |

Fees are critical to realistic results. A strategy that is profitable at 0% fees may be unprofitable at 0.1%.

## Exit Management

Optional stop loss and take profit levels:

| Setting | Default | Description |
|---------|---------|-------------|
| **Stop Loss** | 0% (disabled) | Close position if loss exceeds this percentage |
| **Take Profit** | 0% (disabled) | Close position if profit exceeds this percentage |
| **Trailing Stop** | 0% (disabled) | Stop loss that follows the price as it moves in your favor |

These are applied at the engine level and override any exit logic in your strategy code.

## Next Steps

- [Reading Results](/docs/backtesting/reading-results) — Interpreting your backtest output
- [Trading Modes](/docs/guides/trading-modes) — Detailed guide to trading modes
`,
  },
  {
    id: 'backtesting/reading-results',
    title: 'Reading Backtest Results',
    category: 'backtesting',
    tags: ['results', 'equity curve', 'metrics', 'trades', 'drawdown', 'sharpe', 'win rate'],
    version: '2.0',
    related: ['backtesting/running-a-backtest', 'backtesting/comparing-runs', 'engine/performance-metrics'],
    order: 3,
    description: 'Understanding equity curves, metrics, and trade analysis',
    content: `# Reading Results

After a backtest completes, the results panel shows three main sections: the equity curve, performance metrics, and the trade list.

## Equity Curve

The equity curve shows your portfolio value over time. A good curve trends upward without large drops. Key things to look for:

- **Smooth growth** — Steady upward movement is better than spiky gains
- **Drawdowns** — Large dips indicate risky periods
- **Flat periods** — Long stretches with no change suggest the strategy is inactive

## Key Metrics

| Metric | What It Means | Good Benchmark |
|--------|---------------|----------------|
| **Total Return** | Net profit as a percentage of starting capital | Varies by timeframe |
| **Win Rate** | Percentage of profitable trades | 40-60% is typical |
| **Profit Factor** | Gross profit divided by gross loss | Above 1.5 |
| **Sharpe Ratio** | Risk-adjusted return | Above 1.0 |
| **Max Drawdown** | Largest peak-to-trough decline | Below 20% |
| **Calmar Ratio** | Annual return divided by max drawdown | Above 1.0 |
| **Trade Count** | Total number of completed trades | Enough for significance |

## Reading Trade Lists

Each trade shows:

- **Entry and exit** dates and prices
- **Direction** — long or short
- **P&L** — profit or loss for that trade
- **Duration** — how long the position was held
- **Signal** — what triggered the entry

## Warning Signs

Watch for these red flags that suggest [overfitting](/docs/concepts/overfitting) or unreliable results:

- Very few trades (under 30) — not statistically significant
- Extremely high win rate (above 90%) — likely overfitted
- One or two trades account for most of the profit — not repeatable
- Max drawdown is close to total return — strategy is fragile

## What Comes Next

A single backtest is just a starting point. To build confidence:

1. Change the date range — does it still work on different periods?
2. Try different symbols — does the idea generalize?
3. Run optimization — are the parameters in a [stable region](/docs/concepts/parameter-stability)?
4. Run [Walk-Forward Analysis](/docs/strategy-optimization/walk-forward-analysis) — does it hold up out of sample?

## Next Steps

- [Comparing Runs](/docs/backtesting/comparing-runs) — Side-by-side backtest comparison
- [Performance Metrics](/docs/engine/performance-metrics) — Technical details on each metric
- [Optimization](/docs/strategy-optimization/overview) — Searching for robust parameters
`,
  },
  {
    id: 'backtesting/comparing-runs',
    title: 'Comparing Runs',
    category: 'backtesting',
    tags: ['compare', 'results', 'backtest', 'side by side', 'analysis'],
    version: '2.0',
    related: ['backtesting/reading-results', 'strategy-optimization/overview'],
    order: 4,
    description: 'Comparing backtest results across different configurations',
    content: `# Comparing Runs

After running multiple backtests, you can compare them to understand how different configurations affect performance.

## Why Compare

Comparison helps answer questions like:

- Does the strategy work better on 4h or 1d candles?
- How sensitive is it to parameter changes?
- Does it perform consistently across different time periods?
- What happens with different fee levels?

## Using the Results Tab

The **Results** tab in the right dock lets you browse all previous backtest runs for the current strategy. Each run shows:

- Configuration summary (symbol, interval, date range)
- Key metrics (return, Sharpe, drawdown, trade count)
- Timestamp of the run

Select multiple runs to see them side by side.

## What to Look For

When comparing runs, focus on **consistency**, not peak performance:

- A strategy that returns 30% on one period and -20% on another is unreliable
- A strategy that returns 10-15% consistently across different periods is much stronger
- Small parameter changes should not dramatically change results — if they do, the strategy is [fragile](/docs/concepts/parameter-stability)

## Next Steps

- [Optimization](/docs/strategy-optimization/overview) — Systematic parameter search
- [Walk-Forward Analysis](/docs/strategy-optimization/walk-forward-analysis) — Out-of-sample validation
`,
  },
];
